import { ReactNode } from 'react';
import {
  ApiOutlined,
  FilterOutlined,
  GlobalOutlined,
  DatabaseOutlined,
  CodeOutlined,
  CloudServerOutlined,
  InteractionOutlined,
  ThunderboltOutlined,
  BellOutlined,
  DeploymentUnitOutlined,
  CarOutlined,
  DollarOutlined,
  AccountBookOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  BankOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';

// Branded integration logos, keyed by the stable `nodetype_slug` (package suffix
// with underscores, e.g. `shoptet_premium`). Vite resolves a default asset import
// to a URL string, ready for `<img src>`. Add a new entry by dropping a file in
// `@assets/integration-logos/` and mapping the slug(s) here.
import abraLogo from '@assets/integration-logos/abra.png';
import baselinkerLogo from '@assets/integration-logos/baselinker.png';
import businessCentralLogo from '@assets/integration-logos/business_central.svg';
import dewaltLogo from '@assets/integration-logos/dewalt.svg';
import fioLogo from '@assets/integration-logos/fio.svg';
import odooLogo from '@assets/integration-logos/odoo.png';
import pohodaLogo from '@assets/integration-logos/pohoda.png';
import raynetLogo from '@assets/integration-logos/raynet.png';
import sapLogo from '@assets/integration-logos/sap.svg';
import shopifyLogo from '@assets/integration-logos/shopify.svg';
import shoptetLogo from '@assets/integration-logos/shoptet.svg';

// slug -> branded logo URL. Several slugs (versioned variants) share one logo.
export const BRANDED_LOGOS: Record<string, string> = {
  abraflexi: abraLogo,
  abragen: abraLogo,
  baselinker: baselinkerLogo,
  business_central: businessCentralLogo,
  dewalt: dewaltLogo,
  fio: fioLogo,
  odoo: odooLogo,
  pohoda: pohodaLogo,
  pohoda_sql: pohodaLogo,
  raynet: raynetLogo,
  sap: sapLogo,
  shopify: shopifyLogo,
  shopify_v2: shopifyLogo,
  shoptet_premium: shoptetLogo,
  shoptet_premium_v2: shoptetLogo,
};

// slug -> generic AntD icon, for integrations without a branded logo. This gives
// every technical/utility node a meaningful icon instead of the bare plug. Slugs
// not mapped anywhere fall through to the default `ApiOutlined`.
const GENERIC_ICON_BY_SLUG: Record<string, ReactNode> = {
  // Data stores / databases
  sql: <DatabaseOutlined />,
  pohoda_sql: <DatabaseOutlined />,
  marimex_sql: <DatabaseOutlined />,
  i6_dbcon: <DatabaseOutlined />,
  data_node: <DatabaseOutlined />,
  datanode: <DatabaseOutlined />,
  store_access: <DatabaseOutlined />,

  // Web / transport protocols
  http: <GlobalOutlined />,
  rest: <GlobalOutlined />,
  helios_inuvio_rest: <GlobalOutlined />,
  webhook: <ThunderboltOutlined />,
  endpoint: <DeploymentUnitOutlined />,

  // File transfer
  ftp: <CloudServerOutlined />,
  ftpes: <CloudServerOutlined />,
  sftp: <CloudServerOutlined />,

  // Data manipulation utility nodes
  filter: <FilterOutlined />,
  transform: <InteractionOutlined />,
  code: <CodeOutlined />,

  // Notifications
  notifications_client: <BellOutlined />,
  notifications_nodetype: <BellOutlined />,

  // Shipping / logistics
  balikobot: <CarOutlined />,
  toptrans: <CarOutlined />,

  // ERP / accounting systems
  byznys_accounting: <AccountBookOutlined />,
  byznys_erp: <BankOutlined />,
  k2: <BankOutlined />,
  karat: <BankOutlined />,
  karat_eshop2isk: <BankOutlined />,
  premier: <BankOutlined />,
  qi: <BankOutlined />,
  srm: <BankOutlined />,
  helios_inuvio: <BankOutlined />,
  lobasoft: <BankOutlined />,
  pikito: <BankOutlined />,
  k_motion: <BankOutlined />,
  wpj: <BankOutlined />,

  // Banking / finance
  money_s5: <DollarOutlined />,

  // Eshop / marketplace platforms
  upgates: <ShopOutlined />,
  dotcom: <ShopOutlined />,
  kulina_rest: <ShoppingCartOutlined />,

  // Pricing / market intelligence
  dealavo: <DollarOutlined />,
  chameleoon: <ExperimentOutlined />,

  // Warehouse / fulfillment
  depoto: <DatabaseOutlined />,
};

/**
 * Resolve the generic AntD icon for a slug. Slugs without a branded logo get a
 * category icon (ERP/accounting, eshop, database, protocol, …). Anything not
 * mapped here — e.g. an internal `testnode` — falls back to the neutral
 * `ApiOutlined` plug.
 */
export const getGenericIcon = (slug?: string | null): ReactNode => {
  if (slug && GENERIC_ICON_BY_SLUG[slug]) {
    return GENERIC_ICON_BY_SLUG[slug];
  }
  return <ApiOutlined />;
};
